import { useState, useEffect, useMemo } from "react";
import { sqlReadRaw as sqlRead } from "../../../utils/sqlClient";
import { computeSellMultiplier, computeRollup } from "../../../utils/estimatePricing";

const inputSm = { width: "100%", fontSize: 12, padding: "4px 8px", border: "1px solid var(--cs-border)", borderRadius: 4 };
const label = { fontSize: 11, fontWeight: 600, color: "var(--text3)", display: "block", marginBottom: 4 };
const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "6px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12 };
const fmt = (n) => n == null || isNaN(n) ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const FIELDS = [
  { key: "LaborRate", label: "Labor rate ($/hr)", payloadKey: "labor_rate" },
  { key: "PmFieldGcPct", label: "PM / Field GCs (%)", payloadKey: "pm_field_gc_pct", isPct: true },
  { key: "InsuranceBondPct", label: "Insurance / Bond (%)", payloadKey: "insurance_bond_pct", isPct: true },
  { key: "EscalationPct", label: "Escalation (%)", payloadKey: "escalation_pct", isPct: true },
  { key: "ContingencyPct", label: "Contingency (%)", payloadKey: "contingency_pct", isPct: true },
  { key: "OverheadPct", label: "Overhead (%)", payloadKey: "overhead_pct", isPct: true },
  { key: "ProfitPct", label: "Profit (%)", payloadKey: "profit_pct", isPct: true },
  { key: "DevicesPerCircuit", label: "Devices per circuit", payloadKey: "devices_per_circuit" },
  { key: "VaPerReceptacle", label: "VA per receptacle", payloadKey: "va_per_receptacle" },
  { key: "VaPerLightingFixture", label: "VA per lighting fixture", payloadKey: "va_per_lighting_fixture" },
  { key: "RoutingFactor", label: "Routing factor (x plan distance)", payloadKey: "routing_factor" },
  { key: "VerticalAllowanceFt", label: "Vertical allowance (ft)", payloadKey: "vertical_allowance_ft" },
  { key: "DeviceSpacingFt", label: "Device-to-device spacing (ft)", payloadKey: "device_spacing_ft" },
  { key: "WasteFactorPct", label: "Waste factor (%)", payloadKey: "waste_factor_pct", isPct: true },
];

// The 3 route-length formulas the circuit-generation engine consults
// (src/utils/electricalCircuitEngine.js) — editable here without a code
// deploy (db/29's EstimateCostFormulas). Left unset (or unresolvable) falls back to
// the engine's hardcoded default math.
const FORMULA_FIELDS = [
  { key: "HomerunFormulaID", label: "Homerun formula", payloadKey: "homerun_formula_id" },
  { key: "BranchRouteFormulaID", label: "Branch route formula", payloadKey: "branch_route_formula_id" },
  { key: "FeederRouteFormulaID", label: "Feeder route formula", payloadKey: "feeder_route_formula_id" },
];

export default function ElecSettingsRollupPanel({
  activeRevId, isLocked, payloadOf, settings, onReload, formulas,
  circuits, connections, panels, lineItems, assemblies, items, categories, laborFactors,
}) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);

  async function save() {
    setSaving(true);
    try {
      const payload = { revision_id: activeRevId };
      for (const f of FIELDS) {
        const raw = +form[f.key];
        payload[f.payloadKey] = f.isPct ? raw / 100 : raw;
      }
      for (const f of FORMULA_FIELDS) payload[f.payloadKey] = form[f.key] || null;
      await sqlRead(`EXEC sp_SaveEstimateCostSettings @payload = '${payloadOf(payload)}'`);
      onReload();
    } catch (e) { alert("Error saving settings: " + e.message); }
    finally { setSaving(false); }
  }

  const assembliesById = useMemo(() => new Map(assemblies.map(a => [a.AssemblyID, a])), [assemblies]);
  const itemsById = useMemo(() => new Map(items.map(i => [i.ItemID, i])), [items]);
  const categoriesById = useMemo(() => new Map(categories.map(c => [c.CategoryID, c])), [categories]);
  const laborFactorsById = useMemo(() => new Map(laborFactors.map(f => [f.FactorID, f])), [laborFactors]);
  const formulasById = useMemo(() => new Map(formulas.map(f => [f.FormulaID, f])), [formulas]);

  const rollup = useMemo(() => {
    if (!settings) return null;
    return computeRollup(
      { circuits, connections: connections.filter(c => c.UtilityType === "electrical"), panels, lineItems },
      assembliesById, laborFactorsById, settings,
      { itemsById, categoriesById, formulasById }
    );
  }, [circuits, connections, panels, lineItems, assembliesById, laborFactorsById, itemsById, categoriesById, formulasById, settings]);

  if (!form) return <div style={{ fontSize: 13, color: "var(--text3)" }}>Loading settings…</div>;

  return (
    <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 360px", minWidth: 320 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Commercial Stack & Generation Assumptions</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {FIELDS.map(f => (
            <div key={f.key}>
              <label style={label}>{f.label}</label>
              <input
                disabled={isLocked}
                type="number" step="any"
                value={f.isPct ? +(form[f.key] * 100).toFixed(2) : form[f.key]}
                onChange={e => setForm({ ...form, [f.key]: f.isPct ? +e.target.value / 100 : +e.target.value })}
                style={inputSm}
              />
            </div>
          ))}
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, margin: "18px 0 8px" }}>Circuit Generation Formulas</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {FORMULA_FIELDS.map(f => (
            <div key={f.key}>
              <label style={label}>{f.label}</label>
              <select
                disabled={isLocked}
                value={form[f.key] || ""}
                onChange={e => setForm({ ...form, [f.key]: e.target.value ? +e.target.value : null })}
                style={inputSm}
              >
                <option value="">Built-in default —</option>
                {formulas.map(fm => <option key={fm.FormulaID} value={fm.FormulaID}>{fm.Code}</option>)}
              </select>
            </div>
          ))}
        </div>

        {!isLocked && (
          <button onClick={save} disabled={saving} style={{ marginTop: 14, fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>
            {saving ? "Saving…" : "Save Settings"}
          </button>
        )}
        <div style={{ marginTop: 14, fontSize: 12, color: "var(--text2)" }}>
          Sell multiplier: <strong>{computeSellMultiplier(form).toFixed(4)}×</strong>
        </div>
      </div>

      <div style={{ flex: "1 1 420px", minWidth: 360 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Rollup by System / Load Class / Category</div>
        <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Group</th><th style={th}>Count</th><th style={th}>Direct Cost</th><th style={th}>Sell Value</th></tr></thead>
            <tbody>
              {rollup && Object.entries(rollup.byGroup).map(([key, g]) => (
                <tr key={key}>
                  <td style={td}>{key}</td><td style={td}>{g.count}</td><td style={td}>{fmt(g.directCost)}</td><td style={td}>{fmt(g.sellValue)}</td>
                </tr>
              ))}
              {rollup && !Object.keys(rollup.byGroup).length && (
                <tr><td style={td} colSpan={4}>No priced rows yet — assign an Assembly to circuits/equipment/panels, or add a Line Item.</td></tr>
              )}
            </tbody>
            {rollup && (
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td style={td}>Total</td><td style={td}>{rollup.grandTotal.count}</td><td style={td}>{fmt(rollup.grandTotal.directCost)}</td><td style={td}>{fmt(rollup.grandTotal.sellValue)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
